import { useMemo } from 'react'

import { Button } from '@/components/exercises/Button/Button'

import { StyledEllipsis, StyledPagination } from './Pagination.Styled'

type PaginationProps = {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) => {
  /**
   * Shows up to 9 items so the control never grows too wide:
   * - Total <= 9: all pages shown.
   * - Near start:  1 2 3 4 5 6 7 ... last
   * - Near end:    1 ... n-6 n-5 n-4 n-3 n-2 n-1 last
   * - Middle:      1 ... c-2 c-1 c c+1 c+2 ... last
   */
  const pageNumbers = useMemo<(number | 'ellipsis')[]>(() => {
    const getPageRange = (start: number, end: number) => {
      const range: number[] = []
      for (let i = start; i <= end; i++) {
        range.push(i)
      }
      return range
    }

    if (totalPages <= 9) {
      return getPageRange(1, totalPages)
    } else if (currentPage <= 5) {
      return [...getPageRange(1, 7), 'ellipsis', totalPages]
    } else if (currentPage >= totalPages - 4) {
      return [1, 'ellipsis', ...getPageRange(totalPages - 6, totalPages)]
    } else {
      return [
        1,
        'ellipsis',
        ...getPageRange(currentPage - 2, currentPage + 2),
        'ellipsis',
        totalPages,
      ]
    }
  }, [currentPage, totalPages])

  if (totalPages <= 1) {
    return null
  }

  return (
    <StyledPagination>
      {pageNumbers.map((item, index) => {
        if (item === 'ellipsis') {
          return (
            <StyledEllipsis key={`ellipsis-${index}`}>&hellip;</StyledEllipsis>
          )
        }
        return (
          <Button
            key={item}
            isActive={currentPage === item}
            onClick={() => {
              onPageChange(item)
            }}
            variant="circle"
          >
            {item}
          </Button>
        )
      })}
    </StyledPagination>
  )
}
